import {match} from 'ts-pattern';

import {always} from 'ramda';

import {GetDataByDataQueryApiResponse} from '@dms/api/core';

import {Nullish, parseDate} from 'shared/web';

import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';

/**
 * Converts data grid data to RentalResourceCalendar events with comprehensive task information
 *
 * CRITICAL BUG FIX (2025-01):
 * This function was completely rewritten to fix a critical date handling bug that caused:
 * 1. Events appearing in wrong months/dates
 * 2. Only 1 event showing instead of all 50+ events
 * 3. Multi-day events not working correctly
 *
 * ROOT CAUSE: The original code incorrectly assumed:
 * - scheduledRent/vehicleRent contained only dates (no time)
 * - scheduledReturn/vehicleReturn contained time strings like "10:00 - 14:00"
 *
 * REALITY: Both fields contain full ISO datetime strings like "2025-07-09T05:00:00+00:00"
 *
 * THE FIX: Parse both fields as full datetime strings and use them directly as start/end dates.
 * No more parseDueTime() function that was trying to parse datetimes as time-only strings.
 *
 * @param dataGridData - Data grid response containing rental order rows
 * @returns Array of RentalResourceCalendarEvent objects with correct datetime handling
 */
export function getRentalResourceCalendarDataFromGrid(
  dataGridData: GetDataByDataQueryApiResponse | Nullish
) {
  const result =
    dataGridData?.rows?.reduce<RentalResourceCalendarEvent[]>((events, row) => {
      // FIXED: Both fields contain full ISO datetime strings, not date-only or time-only
      // Example: "2025-07-09T05:00:00+00:00" (was incorrectly parsed before)
      const startDateString = row.cells?.vehicleRent?.value || row.cells?.scheduledRent?.value;

      const endDateString = match(row.cells.state.value?.key)
        .with('draft', always(row.cells?.scheduledReturn?.value))
        .with('reserved', always(row.cells?.scheduledReturn?.value))
        .with('rented', always(row.cells?.scheduledReturn?.value))
        .with(
          'returned',
          always(row.cells?.vehicleReturn?.value || row.cells?.scheduledReturn?.value)
        )
        .with(
          'canceled',
          always(row.cells?.vehicleReturn?.value || row.cells?.scheduledReturn?.value)
        )
        .with(
          'closed',
          always(row.cells?.vehicleReturn?.value || row.cells?.scheduledReturn?.value)
        )
        .otherwise(always(row.cells?.scheduledReturn?.value));

      // FIXED: Parse both as full datetime strings, not separate date+time parsing
      const startDate = parseDate(startDateString);
      const endDate = parseDate(endDateString);

      // Skip rows with invalid or missing start dates
      if (!startDate || isNaN(startDate.getTime())) {
        return events;
      }

      const commonEventProperties = {
        id: row.id,
        title: row.cells?.vehicleTitle?.value?.label,
        groupId: row.cells?.vehicleTitle?.value?.id,
        rentalOrder: {
          number: row.cells?.number?.value,
          vehicleTitle: row.cells?.vehicleTitle?.value?.label,
          vehicleId: row.cells?.vehicleTitle?.value?.id,
          licensePlate: row.cells?.licensePlate?.value,
          vin: row.cells?.vin?.value,
          state: row.cells.state.value?.key,
          stateLabel: row.cells.state.value?.label,
          stateFlagColor: isStyleObject(row.cells.state.style)
            ? row.cells.state.style.flagColor
            : row.cells.state.style?.[0]?.flagColor,
          customerName: row.cells?.customer?.value?.label,
          customerId: row.cells?.customer?.value?.id,
          dailyRate: row.cells?.dailyRate?.value,
          expectedPriceWithVat: row.cells?.expectedPriceWithVat?.value,
          salePriceWithVat: row.cells?.salePriceWithVat?.value,
          expectedPrice: row.cells?.expectedPrice?.value,
          salePrice: row.cells?.salePrice?.value,
          rentalLength: row.cells?.rentalLength?.value,
          rentTypeKey: row.cells?.rentType?.value?.key,
          rentTypeLabel: row.cells?.rentType?.value?.label,
          delayedKey: row.cells?.delayed?.value?.key,
          delayedLabel: row.cells?.delayed?.value?.label,
          delayedFlagColor: isStyleObject(row.cells?.delayed?.style)
            ? row.cells.delayed.style.flagColor
            : row.cells?.delayed?.style?.[0]?.flagColor,
          delayedFlagSubtle: isStyleObject(row.cells?.delayed?.style)
            ? row.cells.delayed.style.flagSubtle
            : row.cells?.delayed?.style?.[0]?.flagSubtle,
          address: row.cells?.address?.value,
          returnAddress: row.cells?.returnAddress?.value,
          serviceOrder: row.cells?.serviceOrder?.value,
          scheduledRent: row.cells?.scheduledRent?.value,
          scheduledReturn: row.cells?.scheduledReturn?.value,
          vehicleRent: row.cells?.vehicleRent?.value,
          vehicleReturn: row.cells?.vehicleReturn?.value,
          rentMileage: row.cells?.rentMileage?.value,
          returnMileage: row.cells?.returnMileage?.value,
          authorId: row.cells?.author?.value?.id,
          authorName: row.cells?.author?.value?.label,
          branchId: row.cells?.branch?.value?.id,
          branchName: row.cells?.branch?.value?.label,
          ownerId: row.cells?.owner?.value?.id,
          ownerName: row.cells?.owner?.value?.label,
          assigneeId: row.cells?.assignee?.value?.id,
          assigneeName: row.cells?.assignee?.value?.label,
          createdAt: row.cells?.createdAt?.value,
          updatedAt: row.cells?.updatedAt?.value,
        },
      };

      // FIXED: Use start and end dates directly (no more parseDueTime parsing)
      // This handles multi-day events correctly (e.g., 9.7. 5:00 to 13.7. 17:00)
      if (endDate) {
        events.push({
          ...commonEventProperties,
          start: startDate,
          end: endDate,
        });
      } else {
        // Fallback: If no end date provided, create all-day event on start date

        // Create new date instances to avoid mutation
        const allDayStart = parseDate(startDate);
        const allDayEnd = parseDate(startDate);
        allDayStart.setHours(0, 0, 0, 0);
        allDayEnd.setHours(0, 0, 0, 0);

        events.push({
          ...commonEventProperties,
          start: allDayStart,
          end: allDayEnd,
        });
      }

      return events;
    }, []) || [];

  return result;
}

/**
 * Type guard to check if cell style is an object (not an array)
 */
function isStyleObject(style: any): style is {flagColor?: string; flagSubtle?: boolean} {
  return style && typeof style === 'object' && !Array.isArray(style);
}
